/**
 * How big the Cross board is, and where its lane marks go.
 *
 * Pure arithmetic, no React and no DOM, so the numbers can be asserted rather
 * than screenshotted. `useBoardGeometry` is the hook that measures a container
 * and feeds it.
 *
 * **The board sizes itself from the space it is given, not from the viewport.**
 * That is the whole rule, and it is what makes one layout serve a phone, a
 * tablet and a laptop instead of three. A cell is 1.4 times as tall as it is
 * wide, so five rows and four gaps are `7 × cellW + 4 × gap` of height against
 * `5 × cellW + 4 × gap` of width: the board is much taller than it is wide and
 * height is what binds on nearly every screen.
 *
 * The two settled board sizes are the sketch's, and this file reproduces both
 * exactly:
 *
 * | | Space given | `cellW` | `gap` | Board |
 * |---|---|---|---|---|
 * | Phone, 390 × 844 | 358 × 571 | 67 | 5 | 355 × 489 |
 * | Laptop, 1280 × 800 | 788 × 680 | 92 | 8 | 492 × 676 |
 *
 * The laptop's 492 + 56 + 380 leaves 176 points of margin either side of a
 * 1280 screen, which is the number `cross-grid.tsx` measured its Table talk
 * rail against. Landing on it is the check that these constants agree with the
 * drawing.
 */

/** Five by five, always. */
export const LANES = [0, 1, 2, 3, 4] as const;

/** A cell is 1.4 times as tall as it is wide: a playing card, near enough. */
export const CELL_ASPECT = 1.4;

/**
 * The gap, as a share of the cell.
 *
 * Fitted to the two boards the sketch settled rather than picked: 0.082 is the
 * only narrow band of ratios that rounds to 5 at a 67-point cell **and** to 8
 * at a 92-point one. `plan.md` § 2.4 writes 0.075, which reproduces the phone
 * and rounds the laptop down to 7 — a four-point difference on a 492-point
 * board, invisible, but the two board sizes are the decision and the ratio is
 * only the device that interpolates between them, so the ratio is what moves.
 */
const GAP_RATIO = 0.082;

/** The gap never closes below this, however small the board gets. */
const MIN_GAP = 5;

/** Below this a cell is not a card, it is a dot. A floor, not a target. */
const MIN_CELL = 28;

/** The cell's corner, as a share of its width: 9 at 67, 12 at 92. */
const RADIUS_RATIO = 0.13;

export interface LaneMarkGeometry {
  /** How thick a mark is drawn. Three at both settled sizes. */
  readonly weight: number;
  /** A row mark's length: 42 percent of the cell's **height**. */
  readonly rowLength: number;
  /** A column mark's length: 42 percent of the cell's **width**. */
  readonly colLength: number;
  /** Clear ground between a mark and the first cell. */
  readonly clear: number;
  /** Where the mark sits relative to the grid box: `-(clear + weight)`. */
  readonly offset: number;
}

export interface BoardGeometry {
  readonly cellW: number;
  readonly cellH: number;
  readonly gap: number;
  readonly radius: number;
  /** Five cells and four gaps across. */
  readonly width: number;
  /** Five cells and four gaps down. The marks cost it nothing. */
  readonly height: number;
  readonly mark: LaneMarkGeometry;
}

/**
 * The lane marks, from the cell alone.
 *
 * Every size is a ratio, so one rule draws both board sizes and a second set
 * of hard numbers for the phone — the bug this shape exists to prevent — is
 * unwritable. `docs/dev/cross_lane_marks.md` is the spec and carries the two
 * revisions this landed between.
 *
 * The lane's **own** dimension is what a length is measured against: reading
 * both off `cellW` drew the row marks at 71 percent of their specified length,
 * on a cell that is 40 percent taller than it is wide.
 */
export function laneMarkGeometry(cellW: number): LaneMarkGeometry {
  const weight = Math.max(3, Math.round(cellW * 0.03));
  const clear = Math.max(5, Math.round(cellW * 0.08));
  return {
    weight,
    rowLength: Math.round(cellW * CELL_ASPECT * 0.42),
    colLength: Math.round(cellW * 0.42),
    clear,
    offset: -(clear + weight),
  };
}

/**
 * The line-score tags a finished grid hangs where its lane marks were.
 *
 * Mitch chose The Tag on 2026-09-07 from the `cross-handover` sketch: each
 * mark grows into a quiet pill holding its line's score in ink. Along the
 * lane a tag is the mark's own 0.42; across it, where the mark was three
 * points, it is 0.23 of a cell with a floor of 15, which is the least that
 * holds two digits at 800. `docs/dev/cross_lane_marks.md` § The hand-over.
 *
 * `room` is the ground outside the board on the tagged side, and it is the
 * phone's whole problem. The laptop has the mark's clearance to spend; a 390
 * phone has 17 points left of its board and about as many under the band, so
 * a 15-point tag has two to divide between the cards and the frame. It splits
 * them: one of clearance from the cells and one of air before the frame's
 * edge, because a tag flush against the edge read as cut off (Mitch,
 * 2026-09-14). One rule, the smaller of the mark's clearance and half of what
 * the room has left, rather than a second set of numbers for the phone. A
 * room too small even for the tag lets it ride onto the veiled cards rather
 * than off the screen.
 */
export interface LineTagGeometry {
  /** How wide a tag is across its lane. */
  readonly across: number;
  /** A row tag's length: the row mark's. */
  readonly rowLength: number;
  /** A column tag's length: the column mark's. */
  readonly colLength: number;
  /** Clear ground between a tag and the first cell. Can go negative. */
  readonly clear: number;
  /** Where the tag sits relative to the grid box: `-(clear + across)`. */
  readonly offset: number;
  /** The numeral's size: 13 on the laptop, 10 on the phone. */
  readonly font: number;
}

export function lineTagGeometry(cellW: number, room = Infinity): LineTagGeometry {
  const mark = laneMarkGeometry(cellW);
  const across = Math.max(15, Math.round(cellW * 0.23));
  const spare = room - across;
  // Half the spare ground goes to the cells and half to the frame's edge; a
  // deficit is taken whole, so the tag rides onto the cards and not off the
  // screen.
  const clear = Math.max(-across, Math.min(mark.clear, Math.floor(spare > 0 ? spare / 2 : spare)));
  return {
    across,
    rowLength: mark.rowLength,
    colLength: mark.colLength,
    clear,
    offset: -(clear + across),
    font: Math.max(10, Math.round(across * 0.62)),
  };
}

/** The centre of lane `index`, measured from the grid box's edge. */
export function laneCentre(index: number, lane: number, gap: number): number {
  return index * (lane + gap) + lane / 2;
}

/** The gap a cell of this width takes. */
function gapFor(cellW: number): number {
  return Math.max(MIN_GAP, Math.round(cellW * GAP_RATIO));
}

/**
 * Clear ground between the board and the crib's slot under it.
 *
 * Twelve at both sizes, which is Mitch's spacing from 2026-09-16: the slot
 * groups with the grid rather than floating between the board and the dock.
 */
export const CRIB_LANE_GAP = 12;

/**
 * How many cell widths of height the board and whatever stands under it take.
 *
 * Five rows of a cell 1.4 times as tall as it is wide is seven cell widths.
 * The crib's slot is one cell width tall, lying down, so a board with the slot
 * under it is eight.
 */
function tallness(laneSlot: boolean): number {
  return laneSlot ? 8 : 7;
}

function fit(availW: number, availH: number, gap: number, laneSlot: boolean): number {
  const spare = 4 * gap;
  return Math.floor(Math.min((availW - spare) / 5, (availH - spare) / tallness(laneSlot)));
}

/**
 * The board that fits the space given, in points.
 *
 * Two passes, not a loop. The gap depends on the cell and the cell depends on
 * the gap, so the cell is fitted once at the minimum gap, the gap is read off
 * that provisional cell, and the cell is fitted again against it. A third pass
 * would oscillate by a point at the boundaries where a gap is about to round
 * up, and a layout that flickers between two sizes as a window is dragged is
 * worse than one that is a point off.
 */
export function boardGeometry(
  availW: number,
  availH: number,
  /**
   * The crib's slot stands under the board, so the cells come in to pay for
   * it: the box gives up `CRIB_LANE_GAP` outright and the board is fitted into
   * eight cell widths of height instead of seven. One flag rather than a
   * second fitter, because everything else about the board is unchanged and a
   * second copy of this arithmetic is how the two sizes start disagreeing.
   */
  laneSlot = false,
): BoardGeometry {
  const box = laneSlot ? Math.max(0, availH - CRIB_LANE_GAP) : availH;
  const provisional = fit(availW, box, MIN_GAP, laneSlot);
  const gap = gapFor(provisional);
  const fitted = fit(availW, box, gap, laneSlot);
  /*
   * The floor is a tap target, and it yields to the box rather than overrunning
   * it.
   *
   * `MIN_CELL` used to be applied unconditionally, which returns a board taller
   * than the space it was asked to fill whenever the space is short: at 667 by
   * 375 — landscape mobile web — the phone box is about 102 points tall, `fit`
   * answers 11, and the floor turns that into a 215-point board hanging out of
   * its slot. The board's holder is `min-h-0 flex-1` and clips nothing, so it
   * overlaps the dock rather than scrolling. The portrait lock that would make
   * this unreachable is native-only (`capacitor.config.ts`), so the browser
   * build meets it.
   *
   * A cell under the floor is a poor board; a board hanging out of its slot is
   * a broken one. Take the floor only when the height can actually hold it.
   */
  const floored = Math.max(MIN_CELL, fitted);
  const fitsFloored =
    Math.round(floored * CELL_ASPECT) * 5 + gap * 4 + (laneSlot ? floored : 0) <= box;
  const cellW = fitsFloored ? floored : Math.max(1, fitted);
  const cellH = Math.round(cellW * CELL_ASPECT);
  return {
    cellW,
    cellH,
    gap,
    radius: Math.round(cellW * RADIUS_RATIO),
    width: cellW * 5 + gap * 4,
    height: cellH * 5 + gap * 4,
    mark: laneMarkGeometry(cellW),
  };
}

/**
 * The crib's slot, from the board it hangs under.
 *
 * One cell width tall and two columns wide, which is the room Mitch allowed on
 * 2026-09-16 when one column could not hold the name and the figure: a slot
 * turned on its side would have been 84 by 60 on the phone and the name would
 * not have fitted in it. It ranges left with the grid, so it reads as the row
 * after the fifth.
 */
export function cribLaneGeometry(board: BoardGeometry): {
  readonly w: number;
  readonly h: number;
  readonly radius: number;
} {
  return { w: board.cellW * 2 + board.gap, h: board.cellW, radius: board.radius };
}

/* ------------------------------------------------------------ arrangements */

/**
 * The earliest the layout changes shape, matching `--breakpoint-tablet`.
 * Enlarged text can keep the measured table in its phone arrangement longer.
 */
export const TABLET = 900;

/** One layout, two arrangements. Measured, not guessed at from the viewport. */
export type Arrangement = 'phone' | 'laptop';

export function arrangementFor(
  width: number,
  columnWidth: number = LAPTOP_FRAME.columnWidth,
  rootFontSize = 16,
): Arrangement {
  const frameWidth =
    (columnWidth + LAPTOP_FRAME.boardGap + LAPTOP_FRAME.sidePad * 2) * (rootFontSize / 16);
  const minimumBoardWidth = MIN_CELL * 5 + MIN_GAP * 4;
  return width >= Math.max(TABLET, frameWidth + minimumBoardWidth) ? 'laptop' : 'phone';
}

/**
 * The phone arrangement's fixed rows.
 *
 * `top` is measured below the status bar rather than from the top of the
 * screen: the sketch drew its own status bar and put the band at 46, which on
 * a real phone is the safe-area inset plus a dozen points of clear ground.
 * `CrossTable` writes `max(safe-top, 34) + 12`, so a browser with no inset
 * lands on the sketch's 46 and a phone lands twelve points under its notch.
 */
export const PHONE_FRAME = {
  /** Clear of the status bar, when there is no inset to measure. */
  statusBar: 34,
  /** Between the status bar and the band. */
  top: 12,
  margin: 16,
  bandHeight: 56,
  /**
   * The band at a four-seat table, where each side stacks its two faces over
   * its score (`cross-four-players`, 2026-10-02): 12 of padding, a 40-point
   * head, 8 of gap, the 24-point score and 12 more, then 12 of clear ground.
   * The two-seat band's pills stop 7 short of its foot; these columns are
   * washed to theirs, and a height-bound board's column marks stand up to 11
   * above its box. The board gives up the difference (the sketch's call 1).
   */
  partnersBandHeight: 108,
  /**
   * The throws' row a three- or four-seat band adds with the crib on: each
   * head stands over its pips (an 8-point row a 4-point gap below), and the
   * column's gap closes from 8 to 4 to pay for most of it
   * (`cross-crib-seats`, call 5, picked 2026-10-03). The band's foot moves
   * down 8 and the board gives up the difference.
   */
  cribPipsRow: 8,
  /**
   * The Table Talk strip under the band, held on every online table so a
   * phrase arriving moves nothing: 6 of air, a one-line bubble of about 34,
   * and room under it for the column lane marks, which stand up to 11 above
   * the board's box (`-(clear + thickness)` at a large cell). The laptop holds
   * its band for the same reason.
   * A width-bound phone barely notices it, because the board is centered in
   * slack it already had. A height-bound one, a tablet in portrait, gives up a
   * few points of cell, which is the price of not dropping the phrase onto
   * the first row.
   */
  talkRow: 56,
  /**
   * 157, not 140: the taller dock is what buys the four things stacked in it
   * their air, and it holds the row the online play clock will need so nothing
   * moves when online lands. `cross-chrome.tsx`'s `PHONE_DOCK_TOP` carries the
   * argument.
   */
  dockHeight: 157,
  /** The dock's foot, off the bottom of the screen. */
  dockBottom: 14,
  /**
   * The column stops widening here. A tablet in portrait is below the
   * breakpoint, and a 736-point band with two seats at either end of it is a
   * scoreboard nobody can read as a pair.
   *
   * 560 is where the board stops being paid for the width, which is what makes
   * it the number rather than a taste: on a 1024-tall tablet the fit turns
   * height-bound at a 102-point cell, and the column would have to reach about
   * 562 for the width to bind at the same size. Past it the board gains nothing
   * and only the band stretches.
   *
   * The band does not take the whole of it. Its own cap is `BAND_MAX_WIDTH`
   * below, because the two are paid for differently: a board is bigger cards,
   * and a band is a fixed pair of heads with the slack going to the score
   * pills.
   */
  maxWidth: 560,
} as const;

/**
 * How wide the phone band is allowed to get.
 *
 * The band was drawn at 358, which is a 390 phone less its margins. Every point
 * it gains past that goes to the two score pills, because the heads are a fixed
 * 56 and the target mark between them is fixed too: at 528 the seat on move had
 * stretched to a 130-point field of wash and the pair stopped reading as a
 * comparison. 420 is about a sixth over the drawn width, which the pills absorb
 * without becoming the loudest thing in the chrome.
 *
 * The board keeps the full column. A band narrower than the board below it is
 * the honest shape here: the band is a scoreboard and the board is the table.
 */
export const BAND_MAX_WIDTH = 420;

/** Laptop frame sizes at a 16px root. Stock spacing utilities scale them with rem. */
export const LAPTOP_FRAME = {
  headerHeight: 64,
  /**
   * Above and below the board. It is what makes a 1280 × 800 laptop land on
   * the sketch's 92-point cell: 800 less the header and 56 of padding is 680,
   * and 680 fits 92 exactly.
   */
  stagePad: 28,
  columnWidth: 380,
  /** Between the board and the station column. */
  boardGap: 56,
  /** Between the scoreboard and the dock inside that column. */
  columnGap: 24,
  /** Least ground either side of the whole row. */
  sidePad: 28,
  /**
   * Table talk's rail over the left edge: `w-88` in `table/talk/TalkTray.tsx`.
   * It may cover the margin and the board, never the station column
   * (`talkRailClears`).
   */
  talkRail: 352,
} as const;

/** What the board is given at the phone arrangement, from the frame's size. */
export function phoneBoardBox(
  width: number,
  height: number,
  /**
   * How tall the slot at the foot actually is, in resolved CSS pixels. The turn dock is 157, the
   * count's ledger is taller and a payout panel taller still, and the board
   * gives up the difference rather than being overlapped by it: at 390 by 844
   * there are 81 points of slack to spend, and on a 375 by 667 phone there
   * are 4.
   */
  slotHeight?: number,
  /**
   * The insets the DOM actually resolved, when the caller has measured them.
   *
   * `CrossTable` pads with `max(--safe-top, 34)` and `--safe-bottom`, and this
   * used to answer with the bare constants — so on any device with a top inset
   * over 34 or a bottom inset at all, the board was sized for a taller box
   * than it was given and overshot its slot by the difference. Defaulting to
   * the constants keeps a caller with nothing to measure exactly where it was.
   */
  insets: { top: number; bottom: number } = { top: 0, bottom: 0 },
  /** The band, dock, maximum width and side margins scale with rem. */
  rootFontSize = 16,
  /** An online table holds the Table Talk strip under the band. */
  talk = false,
  /** Four seats: the band stacks faces over scores and stands taller. */
  partners = false,
  /** With the crib on, a stacked band carries each seat's throws as well. */
  cribPips = false,
): { w: number; h: number } {
  const scale = rootFontSize / 16;
  const column = Math.min(width, PHONE_FRAME.maxWidth * scale);
  const top =
    Math.max(insets.top, PHONE_FRAME.statusBar) +
    PHONE_FRAME.top +
    (partners
      ? PHONE_FRAME.partnersBandHeight + (cribPips ? PHONE_FRAME.cribPipsRow : 0)
      : PHONE_FRAME.bandHeight) *
      scale +
    (talk ? PHONE_FRAME.talkRow * scale : 0);
  const bottom =
    (slotHeight ?? PHONE_FRAME.dockHeight * scale) +
    PHONE_FRAME.dockBottom +
    Math.max(0, insets.bottom);
  return { w: column - PHONE_FRAME.margin * 2 * scale, h: Math.max(0, height - top - bottom) };
}

/** The same at the laptop arrangement. */
export function laptopBoardBox(
  width: number,
  height: number,
  /** The station column's width. A review's is wider than the table's 380. */
  columnWidth: number = LAPTOP_FRAME.columnWidth,
  /** Resolved root font size: the frame uses h-16, w-95/w-105, gap-14 and p-7. */
  rootFontSize = 16,
): { w: number; h: number } {
  const scale = rootFontSize / 16;
  return {
    w: Math.max(
      0,
      width - (columnWidth + LAPTOP_FRAME.boardGap + LAPTOP_FRAME.sidePad * 2) * scale,
    ),
    h: Math.max(0, height - (LAPTOP_FRAME.headerHeight + LAPTOP_FRAME.stagePad * 2) * scale),
  };
}

/**
 * Where the station column starts at the laptop arrangement, from the left
 * edge: the board, the gap and the column stand centered as one row.
 */
export function stationColumnStart(
  width: number,
  height: number,
  columnWidth: number = LAPTOP_FRAME.columnWidth,
  rootFontSize = 16,
  cribLane = false,
): number {
  const scale = rootFontSize / 16;
  const box = laptopBoardBox(width, height, columnWidth, rootFontSize);
  const board = boardGeometry(box.w, box.h, cribLane);
  const row = board.width + (LAPTOP_FRAME.boardGap + columnWidth) * scale;
  return (width - row) / 2 + board.width + LAPTOP_FRAME.boardGap * scale;
}

/**
 * Whether Table talk's laptop rail stays clear of the station column.
 *
 * At the default text size it always does: the column starts at least 492
 * in, and the rail is 352. Enlarged text scales the rail with rem while the
 * board shrinks to fit, so on a laptop just past the breakpoint the rail
 * reached the scores (GPT review, 2026-10-01). An online table that fails
 * this keeps its phone arrangement, where the tray is a sheet over the cards.
 */
export function talkRailClears(
  width: number,
  height: number,
  columnWidth: number = LAPTOP_FRAME.columnWidth,
  rootFontSize = 16,
  cribLane = false,
): boolean {
  const rail = LAPTOP_FRAME.talkRail * (rootFontSize / 16);
  return stationColumnStart(width, height, columnWidth, rootFontSize, cribLane) >= rail;
}

/**
 * A board at a chosen cell width, fitted to nothing.
 *
 * What a caller uses when it has no box to measure — a replay panel in an
 * admin column, the first frame before the observer has run, a static render
 * in a test. The gap, the radius and the marks all follow from the cell, so
 * this is the same board `boardGeometry` would produce given exactly enough
 * room for it.
 */
export function fixedGeometry(cellW: number): BoardGeometry {
  const gap = gapFor(cellW);
  const cellH = Math.round(cellW * CELL_ASPECT);
  return {
    cellW,
    cellH,
    gap,
    radius: Math.round(cellW * RADIUS_RATIO),
    width: cellW * 5 + gap * 4,
    height: cellH * 5 + gap * 4,
    mark: laneMarkGeometry(cellW),
  };
}
